import Box from "@mui/material/Box";
import { useClickAway } from "@uidotdev/usehooks";
import React, { useRef, useState } from "react";

export default function MenuButton({
  icon,
  element,
  position,
  children,
  openState,
}: {
  position: "left" | "right";
  children: React.ReactNode;
  openState: (value: boolean) => void;
  icon?: React.ReactNode;
  element?: React.ReactNode;
}) {
  const [open, setOpen] = useState<boolean>(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useClickAway(() => {
    setOpen(false);
    setTimeout(() => openState(false), 0);
  }) as React.RefObject<HTMLDivElement>;

  const activePositionSx = position === "left" ? { left: 0 } : { right: 0 };

  const menuButtonSx = icon
    ? {
        cursor: "pointer",
        "&:hover": { bgcolor: "var(--color-zinc-700)" },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
      }
    : {};

  const buttonHeight = buttonRef.current?.offsetHeight ? buttonRef.current.offsetHeight + 4 : 40;

  return (
    <Box component="span" sx={{ position: "relative", display: "inline-flex" }} ref={menuRef}>
      <Box
        component="button"
        ref={buttonRef}
        onClick={() => {
          if (!children) return;
          const next = !open;
          setOpen(next);
          setTimeout(() => openState(next), 0);
        }}
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : "false"}
        aria-label={open ? "close menu" : "open menu"}
        sx={{ ...menuButtonSx, border: "none", background: "none", p: 0 }}
      >
        <>{icon ?? element}</>
      </Box>
      {children && (
        <Box
          sx={{
            top: `${buttonHeight + 4}px`,
            ...(open ? { visibility: "visible", opacity: 1 } : { visibility: "hidden", opacity: 0 }),
            ...activePositionSx,
            zIndex: 20,
            position: "absolute",
            width: "max-content",
            transition: "all 150ms",
            bgcolor: "var(--color-zinc-800)",
            border: "1px solid var(--color-zinc-500)",
            boxShadow: "0 0 0 4px rgba(0,0,0,0.5)",
            borderRadius: 1,
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
}
